import { Bell, Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import {
  fetchNotifications,
  markNotificationRead
} from '@/features/notifications/notificationsSlice'
import {
  selectRecentNotifications,
  selectUnreadCount
} from '@/features/notifications/notificationsSelectors'
import type { NotificationSeverity } from '@/features/notifications/types'
import { formatRelativeTime } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

/** Severity → the dot's pill tone (the label text always renders too). */
const SEVERITY_TONE: Record<NotificationSeverity, StatusTone> = {
  critical: 'blocked',
  high: 'blocked',
  warning: 'review',
  info: 'neutral'
}

/**
 * The notification bell (PX-W6.2, AC-6) — additive AppShell header furniture.
 *
 * The unread count comes from `meta.unreadCount` on the fetched list and is
 * recomputed by the slice from the rows after every live bus upsert — never a
 * locally drifted counter. The fetch is `shouldFetch`-guarded (the shared
 * cache the notification centre also reads) and only fires once a session
 * carries an org, so anonymous/guard-level renders never touch the network.
 */
export function NotificationBell() {
  const { t } = useTranslation('notifications')
  const dispatch = useAppDispatch()
  const unreadCount = useAppSelector(selectUnreadCount)
  const recent = useAppSelector(selectRecentNotifications)
  const hasOrg = useAppSelector((s) => s.session.activeOrgId !== null)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasOrg) return
    void dispatch(fetchNotifications())
  }, [hasOrg, dispatch])

  // Close on outside click — the popover affordance of a header control.
  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const onToggleClicked = (): void => setOpen((value) => !value)

  const onMarkReadClicked = (id: string): void => {
    void dispatch(markNotificationRead(id))
  }

  const shownCount = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={onToggleClicked}
        aria-label={t('bell.open')}
        aria-expanded={open}
        className="relative flex h-8 w-8 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread-count"
            aria-label={t('bell.unread', { unread: unreadCount })}
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] font-semibold tabular-nums text-primary-foreground"
          >
            {shownCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-lg border border-border bg-card shadow-lg">
          <div className="border-b border-border px-3 py-2 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {t('title')}
          </div>
          {recent.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t('bell.empty')}</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {recent.map((row) => {
                const severity = row.severity ?? 'info'
                return (
                  <li
                    key={row.id}
                    className="border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <div className="flex items-start gap-2">
                      <StatusPill tone={SEVERITY_TONE[severity]}>
                        {t(`severity.${severity}`)}
                      </StatusPill>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {row.title ?? row.id}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {formatRelativeTime(row.createdAt) ?? ''}
                        </span>
                      </span>
                      {row.status !== 'read' && row.status !== 'dismissed' && (
                        <button
                          type="button"
                          onClick={() => onMarkReadClicked(row.id)}
                          aria-label={t('bell.markRead')}
                          title={t('bell.markRead')}
                          className="rounded-md border border-input p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
          <div className="border-t border-border px-3 py-2">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-foreground underline underline-offset-2 hover:text-primary"
            >
              {t('bell.viewAll')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
